import { useState, useEffect } from "react";
import { X, Download, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia("(display-mode: standalone)").matches || 
                      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem("pwa-prompt-dismissed");
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Listen for the beforeinstallprompt event (Chrome/Edge/etc.)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt if not dismissed recently (within 7 days)
      if (!standalone && daysSinceDismissed > 7) {
        setTimeout(() => setShowInstallPrompt(true), 3000);
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Show iOS instructions if on iOS, not standalone, and not dismissed recently
    if (iOS && !standalone && daysSinceDismissed > 7) {
      setTimeout(() => setShowIOSInstructions(true), 3000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === "accepted") {
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    setShowIOSInstructions(false);
    localStorage.setItem("pwa-prompt-dismissed", Date.now().toString());
  };

  // Don't show anything if already installed
  if (isStandalone) return null;

  // iOS Instructions Banner
  if (showIOSInstructions && isIOS) {
    return (
      <div 
        className="fixed bottom-20 left-4 right-4 z-50 bg-gradient-to-r from-[#1a1a1a] to-[#0f1f0f] border border-[#39FF14]/30 rounded-2xl p-4 shadow-2xl animate-in slide-in-from-bottom-5"
        data-testid="pwa-ios-install-banner"
      >
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-gray-400 hover:text-white p-1"
          data-testid="pwa-dismiss-button"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-[#39FF14]/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-[#39FF14]" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-white font-semibold text-sm mb-1">
              {t("pwa_install_title") || "Install Zig Zag Lawncare"}
            </h3>
            <p className="text-gray-400 text-xs mb-3">
              {t("pwa_ios_instructions") || "Tap the share button and select \"Add to Home Screen\" to install this app."}
            </p>
            
            <div className="flex items-center gap-2 text-xs text-[#39FF14]">
              <span className="bg-[#39FF14]/10 px-2 py-1 rounded">1. Tap</span>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L12 14M12 14L7 9M12 14L17 9M4 18H20V20H4V18Z"/>
              </svg>
              <span className="bg-[#39FF14]/10 px-2 py-1 rounded">2. Add to Home Screen</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Standard Install Banner (Chrome/Edge/etc.)
  if (showInstallPrompt && deferredPrompt) {
    return (
      <div 
        className="fixed bottom-20 left-4 right-4 z-50 bg-gradient-to-r from-[#1a1a1a] to-[#0f1f0f] border border-[#39FF14]/30 rounded-2xl p-4 shadow-2xl animate-in slide-in-from-bottom-5"
        data-testid="pwa-install-banner"
      >
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-gray-400 hover:text-white p-1"
          data-testid="pwa-dismiss-button"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-[#39FF14]/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Download className="w-6 h-6 text-[#39FF14]" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-white font-semibold text-sm mb-1">
              {t("pwa_install_title") || "Install Zig Zag Lawncare"}
            </h3>
            <p className="text-gray-400 text-xs mb-3">
              {t("pwa_install_description") || "Get quick access from your home screen. Works offline!"}
            </p>
            
            <Button
              onClick={handleInstallClick}
              className="bg-[#39FF14] hover:bg-[#39FF14]/90 text-black font-semibold text-sm h-9 px-4"
              data-testid="pwa-install-button"
            >
              {t("pwa_install_button") || "Install App"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PWAInstallPrompt;
